import { Series } from './models/series';
import { SelectionType } from './enums/selection-type';
import type { IGame } from './interfaces/IGame';
import type { ITeam } from './interfaces/ITeam';

/**
 * Minimal interface the overlay requires from the picker. Keeps the overlay
 * testable without instantiating a full sport-specific App.
 */
export interface OverlayPickerSurface {
  /** All currently-generated playoff series, in any order. */
  getAllPlayoffSeries(): Series[];
  /** Apply an outcome to a specific series-game slot via the same path used
   *  by the user click handler. */
  selectSeriesGame(
    gameObservable: KnockoutObservable<IGame>,
    selectedTeam: KnockoutObservable<ITeam> | null,
    selectionType: SelectionType,
    isRandomize: boolean,
    forceSet?: boolean
  ): void;
}

/**
 * Walks `Playoffs.series` sorted by Round and applies each game's outcome
 * to the matching slot in the picker's generated bracket via selectSeriesGame.
 *
 * Correlation:
 * - Series → picker series via TeamIds (unordered pair of TeamID integers)
 * - Game   → game slot via GameNumber (1-based; contract-guaranteed chronological)
 * - Defensive: per-game home/away team must match the picker slot's home/away
 *
 * Iterating in Round order means earlier-round outcomes trigger the picker's
 * reactive bracket-advancement before later-round series are seeded, so later
 * rounds are always present when we reach them.
 *
 * The `roundOrder` parameter maps sport-specific round names to their priority
 * integers (1 = earliest round). NHL passes `NHL_ROUND_ORDER`; NBA will pass
 * `NBA_ROUND_ORDER` (which includes PlayInRound1 at priority 1). Any round
 * name not present in `roundOrder` is treated as priority 99 (sorted last).
 *
 * Never throws — all mismatch paths console.warn and skip.
 */
export function applyOfficialPlayoffOutcomes(
  picker: OverlayPickerSurface,
  playoffs: ILeaguePlayoffs,
  roundOrder: Record<string, number>
): void {
  // The server always sends a Playoffs object — see types.d.ts. Empty `series`
  // means "no playoff data yet" (early in the season, or before the bracket
  // endpoint has populated). Skip — nothing to overlay.
  if (playoffs.series.length === 0) {
    return;
  }

  const sorted = [...playoffs.series].sort(
    (a, b) => (roundOrder[a.round] ?? 99) - (roundOrder[b.round] ?? 99)
  );

  // Compatibility probe: sample the first round present in the official data.
  // Find the lowest-priority round name present — that's the "first round" for
  // this sport (FirstRound for NHL, PlayInRound1 for NBA when play-in games are
  // scheduled, future MLB WildCardRound, etc.). If the picker doesn't have a
  // series for that team pair, the picker's bracket is either:
  //   (a) empty — no regular-season picks made yet, so no seeding, no bracket
  //   (b) seeded from a non-canonical save string (?L= URL or localStorage
  //       with the user's own picks) — so the team pairs differ from IRL
  // Either way, applying official outcomes is meaningless and would just
  // produce a per-series warning for every official series. Silent no-op.
  // (If R1 matches but later rounds diverge, we still warn per series — that
  // case is a real data drift worth surfacing.)
  const minPriority = Math.min(...sorted.map((s) => roundOrder[s.round] ?? 99));
  const firstRound = sorted.find((s) => (roundOrder[s.round] ?? 99) === minPriority);
  if (firstRound) {
    const pickerMatch = findPickerSeriesByTeamPair(
      picker.getAllPlayoffSeries(),
      firstRound.teamIds
    );
    if (!pickerMatch) {
      return;
    }
  }

  for (const officialSeries of sorted) {
    const pickerSeries = findPickerSeriesByTeamPair(
      picker.getAllPlayoffSeries(),
      officialSeries.teamIds
    );
    if (!pickerSeries) {
      console.warn(
        '[official-playoff-overlay] No picker series for team pair',
        officialSeries.teamIds,
        'round',
        officialSeries.round
      );
      continue;
    }

    for (const officialGame of officialSeries.games) {
      applyGameToPickerSlot(picker, pickerSeries, officialGame);
    }
  }
}

/**
 * Finds the picker Series whose home/away team IDs match the given unordered pair.
 * Reads team IDs from the first game slot (always set after series generation).
 */
function findPickerSeriesByTeamPair(
  pickerSeries: Series[],
  officialTeamIds: number[]
): Series | undefined {
  if (officialTeamIds.length !== 2) {
    return undefined;
  }
  const wantA = officialTeamIds[0];
  const wantB = officialTeamIds[1];

  return pickerSeries.find((s) => {
    const firstGame = s.games()[0];
    if (!firstGame) {
      return false;
    }
    const homeTeam = firstGame().home();
    const awayTeam = firstGame().away();
    if (!homeTeam || !awayTeam) {
      return false;
    }
    const homeId = homeTeam.TeamID;
    const awayId = awayTeam.TeamID;
    return (
      (homeId === wantA && awayId === wantB) ||
      (homeId === wantB && awayId === wantA)
    );
  });
}

/**
 * Applies a single official game's outcome to the matching slot in the picker series.
 */
function applyGameToPickerSlot(
  picker: OverlayPickerSurface,
  pickerSeries: Series,
  officialGame: ILeaguePlayoffGame
): void {
  const slotIdx = officialGame.gameNumber - 1; // 1-based → 0-based
  const slotObservable = pickerSeries.gameAt(slotIdx);
  if (!slotObservable) {
    console.warn(
      '[official-playoff-overlay] Picker has no slot for gameNumber',
      officialGame.gameNumber
    );
    return;
  }

  const slotGame = slotObservable();
  const slotHomeId = slotGame.home()?.TeamID;
  const slotAwayId = slotGame.away()?.TeamID;

  if (
    slotHomeId !== officialGame.homeTeamId ||
    slotAwayId !== officialGame.awayTeamId
  ) {
    console.warn(
      '[official-playoff-overlay] Home/away mismatch for gameNumber',
      officialGame.gameNumber,
      '— expected home/away',
      officialGame.homeTeamId,
      '/',
      officialGame.awayTeamId,
      'got',
      slotHomeId,
      '/',
      slotAwayId
    );
    return;
  }

  const { selectionType, selectedTeam } = mapOutcomeToSelection(
    officialGame.outcome,
    slotGame
  );

  if (selectionType === SelectionType.NONE) {
    // Unfinished game — leave slot at its existing value
    return;
  }

  // forceSet=true bypasses selectSeriesGame's toggle-off semantics. Without
  // this flag, applying an outcome that matches a slot's current value (e.g.,
  // user picked correctly via prior URL/localStorage state) would CLEAR the
  // slot instead of confirming it.
  picker.selectSeriesGame(slotObservable, selectedTeam, selectionType, false, /*forceSet*/ true);
}

/**
 * Maps server-side outcome integer → (SelectionType, team observable) pair.
 *
 * Outcome values match the sport-specific PlayoffSource.ComputeOutcome (C#)
 * and SelectionType (TS):
 *   0 = NONE (unfinished)
 *   1 = HOME_WIN (regulation home win)
 *   2 = AWAY_WIN (regulation away win)
 *   4 = HOME_WIN_OVERTIME
 *   5 = HOME_WIN_SHOOTOUT
 *   6 = AWAY_WIN_OVERTIME
 *   7 = AWAY_WIN_SHOOTOUT
 *
 * Relies on the picker's slot home/away matching IRL home/away — which is
 * always the case because PP's tiebreaker is required to agree with the
 * sport's official seeding. A home/away mismatch is a real bug (caught
 * upstream by the defensive check before this function runs) and should not
 * be silently normalized here.
 */
function mapOutcomeToSelection(
  outcome: number,
  slotGame: IGame
): { selectionType: SelectionType; selectedTeam: KnockoutObservable<ITeam> | null } {
  switch (outcome) {
    case SelectionType.HOME_WIN:
      return { selectionType: SelectionType.HOME_WIN, selectedTeam: slotGame.home };
    case SelectionType.AWAY_WIN:
      return { selectionType: SelectionType.AWAY_WIN, selectedTeam: slotGame.away };
    case SelectionType.HOME_WIN_OVERTIME:
      return { selectionType: SelectionType.HOME_WIN_OVERTIME, selectedTeam: slotGame.home };
    case SelectionType.HOME_WIN_SHOOTOUT:
      return { selectionType: SelectionType.HOME_WIN_SHOOTOUT, selectedTeam: slotGame.home };
    case SelectionType.AWAY_WIN_OVERTIME:
      return { selectionType: SelectionType.AWAY_WIN_OVERTIME, selectedTeam: slotGame.away };
    case SelectionType.AWAY_WIN_SHOOTOUT:
      return { selectionType: SelectionType.AWAY_WIN_SHOOTOUT, selectedTeam: slotGame.away };
    default:
      return { selectionType: SelectionType.NONE, selectedTeam: null };
  }
}
